// frontend/src/components/piracy/PiracyMap.tsx
// v4.0 — MultiPolygon+holes, draggable popup (no OL Overlay), forwardRef

import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Point, Polygon, LineString, MultiPolygon } from 'ol/geom';
import Feature from 'ol/Feature';
import { Style, Fill, Stroke, Circle as CircleStyle, Text } from 'ol/style';
import { fromLonLat } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import { Box, Paper, Typography, Chip, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import 'ol/ol.css';

import type { PiracyArea, PiracyReport } from '../../api/piracyApi';

interface PiracyMapProps {
  areas: PiracyArea[];
  reports: PiracyReport[];
}

export interface PiracyMapRef {
  centerOn(lat: number, lon: number, zoom?: number): void;
  showVesselPopup(report: PiracyReport): void;
}

// ── Popup union type (with pixel position) ──
type PopupData =
  | {
      type: 'zone';
      name: string;
      code: string;
      risk: string;
      description: string;
      lat: number;
      lon: number;
      px: number;
      py: number;
    }
  | {
      type: 'vessel';
      name: string;
      lat: number;
      lon: number;
      course: number | null;
      speed: number | null;
      time: string;
      area: string;
      px: number;
      py: number;
    };

// ── Цвета по risk_level ──
const RISK_COLORS: Record<string, { fill: string; stroke: string; label: string }> = {
  HIGH: { fill: 'rgba(229, 57, 53, 0.15)', stroke: 'rgba(229, 57, 53, 0.7)', label: 'Высокий' },
  MEDIUM: { fill: 'rgba(255, 179, 0, 0.12)', stroke: 'rgba(255, 179, 0, 0.7)', label: 'Средний' },
  LOW: { fill: 'rgba(67, 160, 71, 0.10)', stroke: 'rgba(67, 160, 71, 0.6)', label: 'Низкий' },
};

const DEFAULT_RISK = { fill: 'rgba(100,100,255,0.1)', stroke: 'rgba(100,100,255,0.5)', label: '?' };

const RISK_LEGEND: Record<string, string> = {
  HIGH: 'rgba(229, 57, 53, 0.3)',
  MEDIUM: 'rgba(255, 179, 0, 0.3)',
  LOW: 'rgba(67, 160, 71, 0.3)',
};

// ── DMS формат ──
function toDMS(deg: number, isLat: boolean): string {
  const abs = Math.abs(deg);
  const d = Math.floor(abs);
  const m = Math.floor((abs - d) * 60);
  const s = Math.round(((abs - d) * 60 - m) * 60);
  const dir = isLat ? (deg >= 0 ? 'N' : 'S') : deg >= 0 ? 'E' : 'W';
  return `${d}°${m.toString().padStart(2, '0')}'${s.toString().padStart(2, '0')}"${dir}`;
}

// ── Центр полигона ──
function polygonCenter(coords: number[][]): [number, number] {
  let sumLon = 0,
    sumLat = 0;
  const n = coords.length;
  for (const c of coords) {
    sumLon += c[0];
    sumLat += c[1];
  }
  return [sumLon / n, sumLat / n];
}

const PiracyMap = forwardRef<PiracyMapRef, PiracyMapProps>(({ areas, reports }, ref) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  const [ready, setReady] = useState(false);
  const [popup, setPopup] = useState<PopupData | null>(null);

  // ── Закрытие popup ──
  const closePopup = useCallback(() => setPopup(null), []);

  // ── Popup clamped position ──
  const calcPopupPos = useCallback((rawPx: number, rawPy: number) => {
    const mapW = mapRef.current?.clientWidth || 900;
    const mapH = mapRef.current?.clientHeight || 450;
    const PW = 160;
    const PH = 180;

    const px = Math.max(PW, Math.min(mapW - PW, rawPx));

    let py: number;
    if (rawPy - PH - 10 >= 0) {
      py = rawPy - PH - 10;
    } else if (rawPy + PH + 10 <= mapH) {
      py = rawPy + 15;
    } else {
      py = Math.max(5, (mapH - PH) / 2);
    }

    return { px, py };
  }, []);

  // ── Draggable popup (simplified closure approach) ──
  const handlePopupMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startPx = popup?.px || 0;
      const startPy = popup?.py || 0;

      const onMove = (me: MouseEvent) => {
        setPopup((prev) =>
          prev
            ? {
                ...prev,
                px: startPx + (me.clientX - startX),
                py: startPy + (me.clientY - startY),
              }
            : null
        );
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [popup?.px, popup?.py]
  );

  // ── Imperative handle for parent ──
  useImperativeHandle(ref, () => ({
    centerOn(lat, lon, zoom = 10) {
      mapInstanceRef.current?.getView().animate({
        center: fromLonLat([lon, lat]),
        zoom,
        duration: 500,
      });
    },
    showVesselPopup(report) {
      if (report.latitude != null && report.longitude != null) {
        const map = mapInstanceRef.current;
        if (!map) return;
        const pixel = map.getPixelFromCoordinate(fromLonLat([report.longitude, report.latitude]));
        const { px, py } = calcPopupPos(pixel ? pixel[0] : 200, pixel ? pixel[1] : 200);
        setPopup({
          type: 'vessel',
          name: report.vessel?.vessel_name || 'Неизвестно',
          lat: report.latitude,
          lon: report.longitude,
          course: report.course ?? null,
          speed: report.speed ?? null,
          time: report.position_time || report.created_at,
          area: report.area?.code || '—',
          px,
          py,
        });
      }
    },
  }));

  // ── Инициализация карты ──
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = new Map({
      target: mapRef.current,
      layers: [new TileLayer({ source: new OSM() })],
      view: new View({
        center: fromLonLat([48, 8]),
        zoom: 3,
      }),
    });

    mapInstanceRef.current = map;
    setReady(true);

    return () => {
      map.setTarget(undefined);
      mapInstanceRef.current = null;
    };
  }, []);

  // ── Esc закрытие popup ──
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePopup();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [closePopup]);

  // ── Слой зон (Polygon + MultiPolygon) + click handler ──
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !ready) return;

    const features: Feature[] = [];

    for (const area of areas) {
      if (!area.geometry || !area.is_active) continue;
      if (area.code === 'PIRACY_UNKNOWN') continue;

      try {
        const geo = typeof area.geometry === 'string' ? JSON.parse(area.geometry) : area.geometry;
        if (!geo.coordinates?.length) continue;

        const risk = RISK_COLORS[area.risk_level] || DEFAULT_RISK;

        // Поддержка Polygon и MultiPolygon
        let olGeometry: Polygon | MultiPolygon;

        if (geo.type === 'Polygon') {
          const rings = geo.coordinates.map((ring: number[][]) =>
            ring.map((c: number[]) => fromLonLat(c))
          );
          olGeometry = new Polygon(rings);
        } else if (geo.type === 'MultiPolygon') {
          const polys = geo.coordinates.map((poly: number[][][]) =>
            poly.map((ring: number[][]) => ring.map((c: number[]) => fromLonLat(c)))
          );
          olGeometry = new MultiPolygon(polys);
        } else {
          continue;
        }

        const feature = new Feature({ geometry: olGeometry });
        feature.set('areaData', area);
        feature.setStyle(
          new Style({
            fill: new Fill({ color: risk.fill }),
            stroke: new Stroke({ color: risk.stroke, width: 2 }),
            text: new Text({
              text: area.name,
              font: 'bold 12px sans-serif',
              fill: new Fill({ color: '#333' }),
              stroke: new Stroke({ color: '#fff', width: 3 }),
              overflow: true,
            }),
          })
        );
        features.push(feature);
      } catch {
        // skip invalid geometry
      }
    }

    const zonesLayer = new VectorLayer({
      source: new VectorSource({ features }),
      properties: { name: 'piracy-zones' },
    });

    map
      .getLayers()
      .getArray()
      .filter((l) => l.get('name') === 'piracy-zones')
      .forEach((l) => map.removeLayer(l));
    map.addLayer(zonesLayer);

    // ── Click handler: vessel popup (priority) then zone popup ──
    const clickKey = map.on('click', (evt) => {
      let found = false;
      map.forEachFeatureAtPixel(evt.pixel, (f) => {
        if (found) return;
        const reportData = f.get('reportData');
        if (reportData) {
          found = true;
          const { px, py } = calcPopupPos(evt.pixel[0], evt.pixel[1]);
          setPopup({
            type: 'vessel',
            name: reportData.vessel?.vessel_name || 'Неизвестно',
            lat: reportData.latitude,
            lon: reportData.longitude,
            course: reportData.course,
            speed: reportData.speed,
            time: reportData.position_time || reportData.created_at,
            area: reportData.area?.code || '—',
            px,
            py,
          });
          return true;
        }
        const areaData = f.get('areaData');
        if (areaData) {
          found = true;
          const geo =
            typeof areaData.geometry === 'string'
              ? JSON.parse(areaData.geometry)
              : areaData.geometry;
          const outerRing =
            geo.type === 'MultiPolygon' ? geo.coordinates[0][0] : geo.coordinates[0];
          const center = polygonCenter(outerRing);
          const { px, py } = calcPopupPos(evt.pixel[0], evt.pixel[1]);
          setPopup({
            type: 'zone',
            name: areaData.name,
            code: areaData.code,
            risk: areaData.risk_level || '?',
            description: areaData.description || '',
            lat: center[1],
            lon: center[0],
            px,
            py,
          });
          return true;
        }
      });
      if (!found) {
        setPopup(null);
      }
    });

    return () => {
      if (clickKey) unByKey(clickKey);
    };
  }, [areas, ready, calcPopupPos]);

  // ── Слой отчётов (маркеры + треки) ──
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !ready) return;

    const features: Feature[] = [];

    // Группировка по transit_id для треков
    const byTransit: Record<number, PiracyReport[]> = {};
    for (const r of reports) {
      if (r.latitude == null || r.longitude == null) continue;
      const tid = r.transit_id || 0;
      if (!byTransit[tid]) byTransit[tid] = [];
      byTransit[tid].push(r);
    }

    // Треки (линии)
    for (const [, reps] of Object.entries(byTransit)) {
      if (reps.length < 2) continue;
      const sorted = [...reps].sort(
        (a, b) =>
          new Date(a.position_time || a.created_at).getTime() -
          new Date(b.position_time || b.created_at).getTime()
      );
      const lineCoords = sorted.map((r) => fromLonLat([r.longitude!, r.latitude!]));
      const lineFeature = new Feature({ geometry: new LineString(lineCoords) });
      lineFeature.setStyle(
        new Style({
          stroke: new Stroke({ color: 'rgba(33, 150, 243, 0.6)', width: 2, lineDash: [6, 4] }),
        })
      );
      features.push(lineFeature);
    }

    // Маркеры
    for (const report of reports) {
      if (report.latitude == null || report.longitude == null) continue;

      const feature = new Feature({
        geometry: new Point(fromLonLat([report.longitude, report.latitude])),
      });
      feature.set('reportData', report);
      feature.setStyle(
        new Style({
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({ color: '#e53935' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
          }),
          text: new Text({
            text: report.vessel?.vessel_name || '',
            offsetY: -14,
            font: '11px sans-serif',
            fill: new Fill({ color: '#333' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
          }),
        })
      );
      features.push(feature);
    }

    const reportsLayer = new VectorLayer({
      source: new VectorSource({ features }),
      properties: { name: 'piracy-reports' },
    });

    map
      .getLayers()
      .getArray()
      .filter((l) => l.get('name') === 'piracy-reports')
      .forEach((l) => map.removeLayer(l));
    map.addLayer(reportsLayer);
  }, [reports, ready]);

  // ── Подсчёт зон по risk_level ──
  const riskCounts = areas.reduce(
    (acc, a) => {
      if (a.is_active && a.code !== 'PIRACY_UNKNOWN') {
        const r = a.risk_level || 'LOW';
        acc[r] = (acc[r] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <Paper sx={{ p: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Карта HRA-зон
      </Typography>

      <Box sx={{ position: 'relative' }}>
        <Box
          ref={mapRef}
          sx={{ width: '100%', height: 450, borderRadius: 1, overflow: 'hidden' }}
        />

        {/* Draggable popup */}
        {popup && (
          <Paper
            elevation={6}
            sx={{
              position: 'absolute',
              left: popup.px,
              top: popup.py,
              transform: 'translateX(-50%)',
              zIndex: 20,
              p: 1.5,
              minWidth: 240,
              maxWidth: 340,
              cursor: 'grab',
              userSelect: 'text',
              '&:active': { cursor: 'grabbing' },
            }}
            onMouseDown={handlePopupMouseDown}
          >
            <IconButton
              size="small"
              onClick={closePopup}
              sx={{ position: 'absolute', right: 2, top: 2 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {popup.type === 'zone' && (
              <>
                <Typography variant="subtitle2" sx={{ pr: 3, cursor: 'grab' }}>
                  {popup.name}
                </Typography>
                <Chip
                  size="small"
                  label={
                    popup.risk === 'HIGH'
                      ? 'Высокий'
                      : popup.risk === 'MEDIUM'
                        ? 'Средний'
                        : 'Низкий'
                  }
                  color={
                    popup.risk === 'HIGH'
                      ? 'error'
                      : popup.risk === 'MEDIUM'
                        ? 'warning'
                        : 'success'
                  }
                  sx={{ mt: 0.5, mb: 0.5 }}
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  {popup.code}
                </Typography>
                {popup.description && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {popup.description}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 0.5, fontFamily: 'monospace' }}
                >
                  {toDMS(popup.lat, true)} {toDMS(popup.lon, false)}
                </Typography>
              </>
            )}

            {popup.type === 'vessel' && (
              <>
                <Typography variant="subtitle2" sx={{ pr: 3, cursor: 'grab' }}>
                  {popup.name}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {new Date(popup.time).toLocaleString('ru-RU')}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ fontFamily: 'monospace', mt: 0.5 }}
                >
                  {toDMS(popup.lat, true)} {toDMS(popup.lon, false)}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Курс: {popup.course != null ? `${popup.course}°` : '—'} / Скорость:{' '}
                  {popup.speed != null ? `${popup.speed} kn` : '—'}
                </Typography>
                <Chip size="small" label={popup.area} variant="outlined" sx={{ mt: 0.5 }} />
              </>
            )}

            <Typography
              variant="caption"
              display="block"
              color="text.disabled"
              sx={{ mt: 0.5, textAlign: 'center', fontSize: '0.65rem' }}
            >
              Перетащите • Esc закрыть
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Легенда по risk_level */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {(['HIGH', 'MEDIUM', 'LOW'] as const).map((level) => {
          const rc = RISK_COLORS[level];
          return (
            <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  bgcolor: RISK_LEGEND[level] || 'rgba(100,100,255,0.3)',
                  border: `2px solid ${rc.stroke}`,
                  borderRadius: '2px',
                }}
              />
              <Typography variant="caption">
                {rc.label} ({riskCounts[level] || 0})
              </Typography>
            </Box>
          );
        })}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: '#e53935', borderRadius: '50%' }} />
          <Typography variant="caption">Позиция судна ({reports.length})</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 2, bgcolor: 'rgba(33,150,243,0.6)' }} />
          <Typography variant="caption">Трек</Typography>
        </Box>
      </Box>
    </Paper>
  );
});

PiracyMap.displayName = 'PiracyMap';
export default PiracyMap;
